import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { RefreshCw, Plus, Pencil, Trash2, Loader2, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminExchangeRatesPage = () => {
  const { getAuthHeaders } = useAuth();
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ from_currency: 'USD', to_currency: '', rate: '', commission_pct: '0', profit_pct: '0' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/exchange-rates`, { headers: getAuthHeaders() });
      setRates(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm({ from_currency: 'USD', to_currency: '', rate: '', commission_pct: '0', profit_pct: '0' }); setEditing(null); setShowForm(false); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, rate: parseFloat(form.rate), commission_pct: parseFloat(form.commission_pct), profit_pct: parseFloat(form.profit_pct) };
    try {
      if (editing) {
        await axios.put(`${API}/exchange-rates/${editing}`, { rate: payload.rate, commission_pct: payload.commission_pct, profit_pct: payload.profit_pct }, { headers: getAuthHeaders() });
        toast.success('Tasa actualizada');
      } else {
        await axios.post(`${API}/exchange-rates`, payload, { headers: getAuthHeaders() });
        toast.success('Tasa creada');
      }
      resetForm();
      load();
    } catch (e) { toast.error('Error al guardar'); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar esta tasa?')) return;
    try {
      await axios.delete(`${API}/exchange-rates/${id}`, { headers: getAuthHeaders() });
      toast.success('Eliminada');
      load();
    } catch (e) { toast.error('Error'); }
  };

  const startEdit = (r) => {
    setForm({ from_currency: r.from_currency, to_currency: r.to_currency, rate: r.rate?.toString(), commission_pct: r.commission_pct?.toString() || '0', profit_pct: r.profit_pct?.toString() || '0' });
    setEditing(r.id);
    setShowForm(true);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-exchange-rates-page">
      {/* B2B isolation banner — Iter 60. These rates DO NOT affect Aircom Remesas. */}
      <div className="border-l-4 border-amber-500 bg-amber-50 p-4 rounded" data-testid="b2b-only-banner">
        <p className="font-bold text-amber-900 text-sm mb-1">⚠️ Esta página es exclusiva del módulo B2B</p>
        <p className="text-xs text-amber-800 leading-relaxed">
          Las tasas que se editan aquí NO afectan los cálculos de Aircom Remesas (operaciones, emails al cliente, wallets, split 50/25/25).
          Para editar tarifas de remesas, ir a <a href="/admin/zona-de-cambios" className="font-bold underline">💱 Zona de Cambios</a>.
        </p>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Tasas de Cambio</h1>
          <p className="text-slate-500">Configura tasas con comision y ganancia integrada</p>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-blue-700 hover:bg-blue-800" data-testid="add-rate-btn">
          <Plus className="w-4 h-4 mr-2" /> Agregar
        </Button>
      </div>

      {showForm && (
        <Card className="p-6 border-blue-200 bg-blue-50" data-testid="rate-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">{editing ? 'Editar' : 'Nueva'} Tasa de Cambio</h3>
            <button onClick={resetForm}><X className="w-5 h-5 text-slate-400" /></button>
          </div>
          <form onSubmit={handleSave} className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>De (moneda)</Label>
              <Input value={form.from_currency} onChange={(e) => setForm({ ...form, from_currency: e.target.value })} placeholder="USD" required disabled={!!editing} data-testid="rate-from" />
            </div>
            <div className="space-y-2">
              <Label>A (moneda)</Label>
              <Input value={form.to_currency} onChange={(e) => setForm({ ...form, to_currency: e.target.value })} placeholder="VES" required disabled={!!editing} data-testid="rate-to" />
            </div>
            <div className="space-y-2">
              <Label>Tasa base</Label>
              <Input type="number" step="0.0001" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} placeholder="36.50" required data-testid="rate-value" />
            </div>
            <div className="space-y-2">
              <Label>Comision %</Label>
              <Input type="number" step="0.1" value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: e.target.value })} data-testid="rate-commission" />
            </div>
            <div className="space-y-2">
              <Label>Ganancia %</Label>
              <Input type="number" step="0.1" value={form.profit_pct} onChange={(e) => setForm({ ...form, profit_pct: e.target.value })} data-testid="rate-profit" />
            </div>
            {form.rate && form.commission_pct && form.profit_pct && (
              <div className="space-y-2">
                <Label>Tasa final (cliente ve)</Label>
                <div className="h-10 px-3 flex items-center bg-white rounded-md border border-green-300 text-green-700 font-semibold">
                  {(parseFloat(form.rate) * (1 + parseFloat(form.commission_pct || 0) / 100 + parseFloat(form.profit_pct || 0) / 100)).toFixed(4)}
                </div>
              </div>
            )}
            <div className="sm:col-span-2">
              <Button type="submit" disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="rate-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Actualizar' : 'Crear')}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {rates.length === 0 ? (
        <Card className="p-12 text-center border-0 shadow-sm">
          <RefreshCw className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No hay tasas configuradas</p>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rates.map((r, idx) => {
            const finalRate = r.rate * (1 + (r.commission_pct || 0) / 100 + (r.profit_pct || 0) / 100);
            return (
              <Card key={r.id || `${r.from_currency}-${r.to_currency}-${idx}`} className={`p-4 border-0 shadow-sm ${!r.is_active ? 'opacity-50' : ''}`} data-testid={`rate-${r.id || idx}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <RefreshCw className="w-5 h-5 text-blue-500" />
                    <span className="font-bold text-lg">{r.from_currency} → {r.to_currency}</span>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => startEdit(r)} className="p-1 text-blue-500"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(r.id)} className="p-1 text-red-500"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="space-y-1 text-sm">
                  <p>Base: <strong>{r.rate}</strong></p>
                  <p>Comision: {r.commission_pct}% | Ganancia: {r.profit_pct}%</p>
                  <p className="text-green-600 font-semibold">Cliente ve: {finalRate.toFixed(4)}</p>
                  {r.updated_at && <p className="text-xs text-slate-400">Actualizado: {new Date(r.updated_at).toLocaleString('es-ES')}</p>}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminExchangeRatesPage;
